import React, { useState, useMemo } from 'react';
import { BarChart2, Grid, Table as TableIcon, Landmark, TrendingUp, RefreshCw } from 'lucide-react';
import {
  ModalShell,
  InstitutionalKpiCard,
  SegmentedTabs,
} from '../common/ui';
import {
  FiiDiiLatestSnapshot,
  FiiDiiHistoryPayload,
  FiiDiiMonthlyPayload,
} from '../../types';
import { formatCr } from '../../services/fiiDiiService';
import '../../styles/fiiDii.css';

interface FiiDiiExplorerModalProps {
  isOpen: boolean;
  onClose: () => void;
  latest: FiiDiiLatestSnapshot | null;
  history: FiiDiiHistoryPayload | null;
  monthly: FiiDiiMonthlyPayload | null;
  loadingDetails: boolean;
  onRefresh: () => Promise<void>;
  onSwitchToMutualFunds?: () => void;
}

type MatrixViewMode = 'fii' | 'dii' | 'combined';
type ChartViewMode = 'combined' | 'both' | 'fii' | 'dii';
type ActiveTab = 'matrix' | 'chart' | 'table';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const openFiiDiiModal = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('open-fii-dii-modal'));
  }
};

export const FiiDiiExplorerModal: React.FC<FiiDiiExplorerModalProps> = ({
  isOpen,
  onClose,
  latest,
  history,
  monthly,
  loadingDetails,
  onRefresh,
  onSwitchToMutualFunds,
}) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('table');
  const [selectedMonthFilter, setSelectedMonthFilter] = useState<string>('all');
  const [matrixMode, setMatrixMode] = useState<MatrixViewMode>('combined');
  const [chartMode, setChartMode] = useState<ChartViewMode>('combined');
  const [chartDays, setChartDays] = useState<number>(20);
  const [hoveredChartDate, setHoveredChartDate] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);


  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Available months for filtering daily datewise records
  const availableMonths = useMemo(() => {
    if (!history?.records) return [];
    const map: Record<string, number> = {};
    history.records.forEach((r) => {
      const ym = r.date.slice(0, 7);
      map[ym] = (map[ym] || 0) + 1;
    });
    return Object.entries(map).map(([key, count]) => {
      const [y, m] = key.split('-');
      const monthName = MONTH_NAMES[parseInt(m, 10) - 1] || m;
      return {
        key,
        label: `${monthName} ${y}`,
        count,
      };
    });
  }, [history?.records]);

  // Filtered daily datewise records
  const displayedDailyRecords = useMemo(() => {
    if (!history?.records) return [];
    if (selectedMonthFilter === 'all') return history.records;
    return history.records.filter((r) => r.date.startsWith(selectedMonthFilter));
  }, [history?.records, selectedMonthFilter]);

  // Formatter for trading date display (e.g., "06 Oct 2026") and weekday ("Tue")
  const formatDateDisplay = (isoDate: string) => {
    try {
      const [y, m, d] = isoDate.split('-');
      const dateObj = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
      const weekday = dateObj.toLocaleDateString('en-IN', { weekday: 'short' });
      const monthStr = MONTH_NAMES[parseInt(m, 10) - 1] || m;
      return {
        formatted: `${d} ${monthStr} ${y}`,
        weekday,
      };
    } catch {
      return { formatted: isoDate, weekday: '' };
    }
  };

  // Active trading session date (e.g. "2026-10-08")
  const activeSessionDate = latest?.date || history?.records?.[0]?.date;
  const activeYear = activeSessionDate && activeSessionDate.length >= 7
    ? parseInt(activeSessionDate.slice(0, 4), 10)
    : undefined;
  const activeMonth = activeSessionDate && activeSessionDate.length >= 7
    ? parseInt(activeSessionDate.slice(5, 7), 10)
    : undefined;

  // Reconciled monthly items: ensures active ongoing month always reflects authoritative MTD flows
  const reconciledMonthlyData = useMemo(() => {
    if (!monthly?.monthlyData) return [];

    const items = monthly.monthlyData.map((item) => ({ ...item }));

    if (activeYear !== undefined && activeMonth !== undefined) {
      let mtdFii = latest?.kpis?.mtdNet?.fii;
      let mtdDii = latest?.kpis?.mtdNet?.dii;
      let mtdComb = latest?.kpis?.mtdNet?.combined;

      // Fallback: sum directly from daily history records if mtdNet KPI is absent
      if (mtdFii === undefined || mtdDii === undefined || mtdComb === undefined) {
        const activePrefix = `${activeYear}-${String(activeMonth).padStart(2, '0')}`;
        const activeRecords = (history?.records || []).filter((r) => r.date.startsWith(activePrefix));
        if (activeRecords.length > 0) {
          mtdFii = Math.round(activeRecords.reduce((acc, r) => acc + r.fiiNet, 0) * 100) / 100;
          mtdDii = Math.round(activeRecords.reduce((acc, r) => acc + r.diiNet, 0) * 100) / 100;
          mtdComb = Math.round((mtdFii + mtdDii) * 100) / 100;
        }
      }

      if (mtdFii !== undefined && mtdDii !== undefined) {
        const combVal = mtdComb !== undefined ? mtdComb : Math.round((mtdFii + mtdDii) * 100) / 100;
        const existingIdx = items.findIndex((m) => m.year === activeYear && m.month === activeMonth);

        if (existingIdx >= 0) {
          items[existingIdx] = {
            ...items[existingIdx],
            fiiNet: mtdFii,
            diiNet: mtdDii,
            combinedNet: combVal,
          };
        } else {
          items.push({
            year: activeYear,
            month: activeMonth,
            monthName: MONTH_NAMES[activeMonth - 1] || `M${activeMonth}`,
            monthKey: `${activeYear}-${String(activeMonth).padStart(2, '0')}`,
            fiiNet: mtdFii,
            diiNet: mtdDii,
            combinedNet: combVal,
          });
        }
      }
    }

    return items;
  }, [monthly?.monthlyData, activeYear, activeMonth, latest?.kpis?.mtdNet, history?.records]);

  // Group monthly items by year for matrix display
  const matrixData = useMemo(() => {
    if (reconciledMonthlyData.length === 0) return [];

    const years = Array.from(new Set(reconciledMonthlyData.map((m) => m.year))).sort((a, b) => b - a);

    return years.map((year) => {
      const yearMonths = reconciledMonthlyData.filter((m) => m.year === year);

      const monthsMap: Record<number, number> = {};
      yearMonths.forEach((m) => {
        monthsMap[m.month] =
          matrixMode === 'fii' ? m.fiiNet : matrixMode === 'dii' ? m.diiNet : m.combinedNet;
      });

      // Full Year total is the exact mathematical sum of months displayed in that row
      const yearTotal = Object.values(monthsMap).reduce((acc, val) => acc + (val ?? 0), 0);

      return {
        year,
        months: monthsMap,
        total: Math.round(yearTotal),
      };
    });
  }, [reconciledMonthlyData, matrixMode]);

  // Full-year / YTD cumulative figures for Card 4
  const ytdCardData = useMemo(() => {
    if (activeYear && reconciledMonthlyData.length > 0) {
      const activeYearMonths = reconciledMonthlyData.filter((m) => m.year === activeYear);
      if (activeYearMonths.length > 0) {
        const fii = Math.round(activeYearMonths.reduce((acc, m) => acc + m.fiiNet, 0) * 100) / 100;
        const dii = Math.round(activeYearMonths.reduce((acc, m) => acc + m.diiNet, 0) * 100) / 100;
        const combined = Math.round((fii + dii) * 100) / 100;
        return { fii, dii, combined };
      }
    }

    return {
      fii: latest?.kpis.ytdNet?.fii ?? 0,
      dii: latest?.kpis.ytdNet?.dii ?? 0,
      combined: latest?.kpis.ytdNet?.combined ?? 0,
    };
  }, [activeYear, reconciledMonthlyData, latest?.kpis.ytdNet]);

  // Chart data calculation
  const chartItems = useMemo(() => {
    if (!history?.records) return [];
    return history.records.slice(0, chartDays).reverse();
  }, [history, chartDays]);

  const maxChartVal = useMemo(() => {
    if (chartItems.length === 0) return 10000;
    if (chartMode === 'combined') {
      const maxVal = Math.max(...chartItems.map((c) => Math.abs(c.combinedNet)));
      return maxVal > 0 ? maxVal * 1.15 : 10000;
    }
    if (chartMode === 'fii') {
      const maxVal = Math.max(...chartItems.map((c) => Math.abs(c.fiiNet)));
      return maxVal > 0 ? maxVal * 1.15 : 10000;
    }
    if (chartMode === 'dii') {
      const maxVal = Math.max(...chartItems.map((c) => Math.abs(c.diiNet)));
      return maxVal > 0 ? maxVal * 1.15 : 10000;
    }
    // 'both' comparison mode
    const maxVal = Math.max(
      ...chartItems.map((c) => Math.max(Math.abs(c.fiiNet), Math.abs(c.diiNet)))
    );
    return maxVal > 0 ? maxVal * 1.15 : 10000;
  }, [chartItems, chartMode]);

  // Period statistics for visible chart days
  const chartPeriodStats = useMemo(() => {
    if (chartItems.length === 0) return null;
    const totalCombined = chartItems.reduce((acc, c) => acc + c.combinedNet, 0);
    const totalFii = chartItems.reduce((acc, c) => acc + c.fiiNet, 0);
    const totalDii = chartItems.reduce((acc, c) => acc + c.diiNet, 0);

    const activeTotal =
      chartMode === 'fii'
        ? totalFii
        : chartMode === 'dii'
        ? totalDii
        : totalCombined;

    const activeBullDays = chartItems.filter((c) => {
      const v =
        chartMode === 'fii'
          ? c.fiiNet
          : chartMode === 'dii'
          ? c.diiNet
          : c.combinedNet;
      return v > 0;
    }).length;

    const activeBearDays = chartItems.filter((c) => {
      const v =
        chartMode === 'fii'
          ? c.fiiNet
          : chartMode === 'dii'
          ? c.diiNet
          : c.combinedNet;
      return v < 0;
    }).length;

    return {
      activeTotal: Math.round(activeTotal * 100) / 100,
      activeBullDays,
      activeBearDays,
      totalCombined: Math.round(totalCombined * 100) / 100,
      totalFii: Math.round(totalFii * 100) / 100,
      totalDii: Math.round(totalDii * 100) / 100,
      bullDays: chartItems.filter((c) => c.combinedNet > 0).length,
      bearDays: chartItems.filter((c) => c.combinedNet < 0).length,
      totalDays: chartItems.length,
    };
  }, [chartItems, chartMode]);

  // Active item for the HUD (hovered date or latest date in chart)
  const activeHoverItem = useMemo(() => {
    if (chartItems.length === 0) return null;
    if (hoveredChartDate) {
      const found = chartItems.find((c) => c.date === hoveredChartDate);
      if (found) return found;
    }
    return chartItems[chartItems.length - 1]; // default to latest
  }, [chartItems, hoveredChartDate]);

  // Compact formatter for bar values so they fit cleanly without collision
  const formatBarValue = (val: number) => {
    const abs = Math.abs(val);
    const sign = val >= 0 ? '+' : '-';
    if (abs >= 1000) {
      return `${sign}${(abs / 1000).toFixed(1)}k`;
    }
    return `${sign}${Math.round(abs)}`;
  };

  if (!isOpen || typeof document === 'undefined') return null;

  const getHeatClass = (val: number | undefined) => {
    if (val === undefined || val === null) return 'cell-empty';
    if (val > 25000) return 'cell-pos-high';
    if (val > 5000) return 'cell-pos-med';
    if (val > 0) return 'cell-pos-low';
    if (val < -25000) return 'cell-neg-high';
    if (val < -5000) return 'cell-neg-med';
    if (val < 0) return 'cell-neg-low';
    return 'cell-empty';
  };

  const getTableLiquidityHeatClass = (val: number | undefined) => {
    if (val === undefined || val === null || val === 0) return '';
    const abs = Math.abs(val);
    if (val > 0) {
      if (abs >= 2500) return 'ns-fiidii-heat--pos-3';
      if (abs >= 1000) return 'ns-fiidii-heat--pos-2';
      return 'ns-fiidii-heat--pos-1';
    }
    if (abs >= 2500) return 'ns-fiidii-heat--neg-3';
    if (abs >= 1000) return 'ns-fiidii-heat--neg-2';
    return 'ns-fiidii-heat--neg-1';
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Institutional Cash Market Activity"
      badge="FII & DII"
      badgeBg="var(--ns-surface-subtle, #f1f5f9)"
      badgeColor="var(--ns-text-secondary, #475569)"
      subtitle="14-Year Macro Matrix (2012–2026) • Rolling Inflows &amp; Daily Institutional Pulse"
      icon={<Landmark size={17} />}
      iconStyle={{ background: 'var(--ns-surface-subtle, #f1f5f9)', color: 'var(--ns-text-secondary, #475569)', border: '1px solid var(--ns-border-default, #e2e8f0)' }}
      maxWidth="1140px"
      overlayClassName="ns-fiidii-modal-overlay"
      dialogClassName="ns-fiidii-modal"
      headerClassName="ns-fiidii-modal__header"
      bodyClassName="ns-fiidii-modal__body"
      footerClassName="ns-fiidii-modal__footer"
      onRefresh={handleRefresh}
      isRefreshing={isRefreshing}
      closeAriaLabel="Close"
      refreshAriaLabel="Refresh Data"
      headerActions={
        <>
          {latest?.displayDate && (
            <span className="ns-fiidii-modal__date-badge">
              <span className="ns-fiidii-pulse-dot" />
              Session: {latest.displayDate}
            </span>
          )}
          {onSwitchToMutualFunds && (
            <button
              type="button"
              className="ns-fiidii-header-nav-btn"
              onClick={onSwitchToMutualFunds}
              title="Switch to Mutual Funds & SIP Trajectory Desk (5-Year AMFI)"
            >
              <TrendingUp size={12} /> Mutual Funds &amp; SIPs (5-Yr) &rarr;
            </button>
          )}
        </>
      }
      footerLeft={<span>All values in ₹ Crores. Source: NSE &amp; BSE Institutional Cash Feeds.</span>}
      footerRight={<span>Heatmap intensity denotes net institutional liquidity</span>}
    >
      {/* Top 4 KPI Cards */}
      <div className="ns-fiidii-kpis ns-inst-kpi-grid">
        {/* Today */}
        <InstitutionalKpiCard
          className="ns-fiidii-kpi-card"
          title="Today's Net Flow"
          badge="CASH EOD"
          value={formatCr(latest?.combinedNet, true)}
          valueVariant={(latest?.combinedNet ?? 0) >= 0 ? 'pos' : 'neg'}
        >
          <div className="ns-fiidii-kpi-card__breakdown">
            <span className="ns-fiidii-kpi-card__subitem">
              <span className="ns-fiidii-kpi-card__sublabel">FII:</span>
              <strong className={(latest?.fii.net ?? 0) >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                {formatCr(latest?.fii.net, true)}
              </strong>
            </span>
            <span className="ns-fiidii-kpi-card__subitem">
              <span className="ns-fiidii-kpi-card__sublabel">DII:</span>
              <strong className={(latest?.dii.net ?? 0) >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                {formatCr(latest?.dii.net, true)}
              </strong>
            </span>
          </div>
        </InstitutionalKpiCard>

        {/* 5-Day */}
        <InstitutionalKpiCard
          className="ns-fiidii-kpi-card"
          title="5-Day Rolling Net"
          badge="5D ROLLING"
          value={formatCr(latest?.kpis.d5Net?.combined, true)}
          valueVariant={(latest?.kpis.d5Net?.combined ?? 0) >= 0 ? 'pos' : 'neg'}
        >
          <div className="ns-fiidii-kpi-card__breakdown">
            <span className="ns-fiidii-kpi-card__subitem">
              <span className="ns-fiidii-kpi-card__sublabel">FII:</span>
              <strong className={(latest?.kpis.d5Net?.fii ?? 0) >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                {formatCr(latest?.kpis.d5Net?.fii, true)}
              </strong>
            </span>
            <span className="ns-fiidii-kpi-card__subitem">
              <span className="ns-fiidii-kpi-card__sublabel">DII:</span>
              <strong className={(latest?.kpis.d5Net?.dii ?? 0) >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                {formatCr(latest?.kpis.d5Net?.dii, true)}
              </strong>
            </span>
          </div>
        </InstitutionalKpiCard>

        {/* MTD */}
        <InstitutionalKpiCard
          className="ns-fiidii-kpi-card"
          title="MTD Net Flow"
          badge={activeMonth && activeYear ? `${MONTH_NAMES[activeMonth - 1].toUpperCase()} ${activeYear}` : 'MTD'}
          value={formatCr(latest?.kpis.mtdNet?.combined, true)}
          valueVariant={(latest?.kpis.mtdNet?.combined ?? 0) >= 0 ? 'pos' : 'neg'}
        >
          <div className="ns-fiidii-kpi-card__breakdown">
            <span className="ns-fiidii-kpi-card__subitem">
              <span className="ns-fiidii-kpi-card__sublabel">FII:</span>
              <strong className={(latest?.kpis.mtdNet?.fii ?? 0) >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                {formatCr(latest?.kpis.mtdNet?.fii, true)}
              </strong>
            </span>
            <span className="ns-fiidii-kpi-card__subitem">
              <span className="ns-fiidii-kpi-card__sublabel">DII:</span>
              <strong className={(latest?.kpis.mtdNet?.dii ?? 0) >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                {formatCr(latest?.kpis.mtdNet?.dii, true)}
              </strong>
            </span>
          </div>
        </InstitutionalKpiCard>

        {/* YTD */}
        <InstitutionalKpiCard
          className="ns-fiidii-kpi-card"
          title={activeYear ? `YTD ${activeYear} Net` : 'YTD Net'}
          badge={activeYear ? `CY ${activeYear}` : 'CY 2026'}
          value={formatCr(ytdCardData.combined, true)}
          valueVariant={(ytdCardData.combined ?? 0) >= 0 ? 'pos' : 'neg'}
        >
          <div className="ns-fiidii-kpi-card__breakdown">
            <span className="ns-fiidii-kpi-card__subitem">
              <span className="ns-fiidii-kpi-card__sublabel">FII:</span>
              <strong className={(ytdCardData.fii ?? 0) >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                {formatCr(ytdCardData.fii, true)}
              </strong>
            </span>
            <span className="ns-fiidii-kpi-card__subitem">
              <span className="ns-fiidii-kpi-card__sublabel">DII:</span>
              <strong className={(ytdCardData.dii ?? 0) >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                {formatCr(ytdCardData.dii, true)}
              </strong>
            </span>
          </div>
        </InstitutionalKpiCard>
      </div>

      {/* Unified High-Density Toolbar: Tabs on Left + Contextual Controls on Right in Single Row */}
      <div className="ns-fiidii-toolbar">
        <SegmentedTabs<ActiveTab>
          className="ns-fiidii-tabs"
          listClassName="ns-fiidii-tab-buttons"
          buttonClassName="ns-fiidii-tab-btn"
          badgeClassName="ns-fiidii-tab-count"
          tabs={[
            {
              id: 'table',
              label: 'Daily Datewise Records',
              icon: <TableIcon size={13} />,
              badge: history?.records ? history.records.length : undefined,
            },
            {
              id: 'chart',
              label: 'Daily Trend Chart',
              icon: <BarChart2 size={13} />,
            },
            {
              id: 'matrix',
              label: '14-Year Monthly Matrix (2012–2026)',
              icon: <Grid size={13} />,
            },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

        <div className="ns-fiidii-toolbar-actions">
          {activeTab === 'table' && availableMonths.length > 1 && (
            <div className="ns-fiidii-filter-group">
              <button
                className={`ns-fiidii-filter-btn ${selectedMonthFilter === 'all' ? 'is-active' : ''}`}
                onClick={() => setSelectedMonthFilter('all')}
              >
                All Dates ({history?.records?.length || 0})
              </button>
              {availableMonths.map((m) => (
                <button
                  key={m.key}
                  className={`ns-fiidii-filter-btn ${selectedMonthFilter === m.key ? 'is-active' : ''}`}
                  onClick={() => setSelectedMonthFilter(m.key)}
                >
                  {m.label} ({m.count})
                </button>
              ))}
            </div>
          )}

          {activeTab === 'chart' && (
            <div className="ns-fiidii-filter-group">
              <button
                type="button"
                className={`ns-fiidii-filter-btn ${chartMode === 'combined' ? 'is-active' : ''}`}
                onClick={() => setChartMode('combined')}
                title="Net Combined institutional market flow (FII + DII)"
              >
                Combined Net
              </button>
              <button
                type="button"
                className={`ns-fiidii-filter-btn ${chartMode === 'both' ? 'is-active' : ''}`}
                onClick={() => setChartMode('both')}
                title="Side-by-side comparative bars for FII vs DII"
              >
                FII vs DII
              </button>
              <button
                type="button"
                className={`ns-fiidii-filter-btn ${chartMode === 'fii' ? 'is-active' : ''}`}
                onClick={() => setChartMode('fii')}
                title="Foreign Institutional Investors net cash flow"
              >
                FII Net (Cash)
              </button>
              <button
                type="button"
                className={`ns-fiidii-filter-btn ${chartMode === 'dii' ? 'is-active' : ''}`}
                onClick={() => setChartMode('dii')}
                title="Domestic Institutional Investors net cash flow"
              >
                DII Net (Cash)
              </button>
              <span className="ns-fiidii-filter-divider" />
              {[10, 20, 30].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`ns-fiidii-filter-btn ${chartDays === d ? 'is-active' : ''}`}
                  onClick={() => setChartDays(d)}
                >
                  {d} Days
                </button>
              ))}
            </div>
          )}

          {activeTab === 'matrix' && (
            <div className="ns-fiidii-filter-group">
              <button
                className={`ns-fiidii-filter-btn ${matrixMode === 'combined' ? 'is-active' : ''}`}
                onClick={() => setMatrixMode('combined')}
              >
                Combined Net
              </button>
              <button
                className={`ns-fiidii-filter-btn ${matrixMode === 'fii' ? 'is-active' : ''}`}
                onClick={() => setMatrixMode('fii')}
              >
                FII Net
              </button>
              <button
                className={`ns-fiidii-filter-btn ${matrixMode === 'dii' ? 'is-active' : ''}`}
                onClick={() => setMatrixMode('dii')}
              >
                DII Net
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab 1: Daily Datewise Records Table */}
      {activeTab === 'table' && (
        <div className="ns-fiidii-table-wrap">
          {loadingDetails && !history ? (
            <div className="ns-fiidii-loading-state">
              <RefreshCw size={22} className="animate-spin" />
              <span>Loading datewise institutional records...</span>
            </div>
          ) : displayedDailyRecords.length === 0 ? (
            <div className="ns-fiidii-empty-filter">
              <p>No datewise trade records found for <strong>{selectedMonthFilter}</strong>.</p>
              <button
                type="button"
                className="ns-fiidii-filter-btn is-active"
                onClick={() => setSelectedMonthFilter('all')}
              >
                Show All Dates ({history?.records?.length || 0})
              </button>
            </div>
          ) : (
            <table className="ns-fiidii-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>FII Buy (₹ Cr)</th>
                  <th>FII Sell (₹ Cr)</th>
                  <th>FII Net</th>
                  <th className="ns-fiidii-th--dii-start">DII Buy (₹ Cr)</th>
                  <th>DII Sell (₹ Cr)</th>
                  <th>DII Net</th>
                  <th className="ns-fiidii-th--comb">Combined Net</th>
                  <th className="ns-fiidii-th--pulse">Flow Pulse</th>
                </tr>
              </thead>
              <tbody>
                {displayedDailyRecords.map((row) => {
                  const { formatted, weekday } = formatDateDisplay(row.date);
                  const isLatest = row.date === latest?.date;
                  const fiiHeatClass = getTableLiquidityHeatClass(row.fiiNet);
                  const diiHeatClass = getTableLiquidityHeatClass(row.diiNet);
                  const combHeatClass = getTableLiquidityHeatClass(row.combinedNet);
                  const pulsePct = Math.min(100, Math.max(16, (Math.abs(row.combinedNet) / 4500) * 100));

                  return (
                    <tr key={row.date} className={isLatest ? 'ns-fiidii-row--latest' : ''}>
                      <td style={{ fontWeight: 600 }}>
                        <div className="ns-fiidii-date-cell">
                          <span className="ns-fiidii-date-str">{formatted}</span>
                          <span className="ns-fiidii-weekday-badge">{weekday}</span>
                          {isLatest && <span className="ns-fiidii-latest-badge">LATEST</span>}
                        </div>
                      </td>
                      <td>{formatCr(row.fiiBuy).replace(' Cr', '')}</td>
                      <td>{formatCr(row.fiiSell).replace(' Cr', '')}</td>
                      <td className={`${row.fiiNet >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'} ${fiiHeatClass}`}>
                        {formatCr(row.fiiNet, true)}
                      </td>
                      <td className="ns-fiidii-td--dii-start">{formatCr(row.diiBuy).replace(' Cr', '')}</td>
                      <td>{formatCr(row.diiSell).replace(' Cr', '')}</td>
                      <td className={`${row.diiNet >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'} ${diiHeatClass}`}>
                        {formatCr(row.diiNet, true)}
                      </td>
                      <td className={`ns-fiidii-td--comb ${row.combinedNet >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'} ${combHeatClass}`}>
                        {formatCr(row.combinedNet, true)}
                      </td>
                      <td className="ns-fiidii-td--pulse">
                        <div
                          className="ns-fiidii-pulse-bar-wrap"
                          title={`Combined Net: ${formatCr(row.combinedNet, true)}`}
                        >
                          <div
                            className={`ns-fiidii-pulse-fill ${row.combinedNet >= 0 ? 'ns-fiidii-pulse-fill--pos' : 'ns-fiidii-pulse-fill--neg'}`}
                            style={{ width: `${pulsePct}%` }}
                          />
                          <span className="ns-fiidii-pulse-text">
                            {row.combinedNet >= 0 ? 'ACCUM' : 'DISTR'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

          {/* Tab 2: Daily Trend Chart */}
          {activeTab === 'chart' && (
            <div className="ns-fiidii-chart-container">
              {/* HUD: Interactive Date & Value breakdown */}
              {activeHoverItem && (
                <div className="ns-fiidii-chart-hud">
                  <div className="ns-fiidii-chart-hud__date">
                    <span>📅 {formatDateDisplay(activeHoverItem.date).formatted}</span>
                    <span style={{ opacity: 0.6 }}>({formatDateDisplay(activeHoverItem.date).weekday})</span>
                    {activeHoverItem.date === chartItems[chartItems.length - 1]?.date && (
                      <span className="ns-fiidii-latest-badge" style={{ fontSize: '9px', padding: '1px 5px', marginLeft: 4 }}>
                        LATEST
                      </span>
                    )}
                  </div>
                  <div className="ns-fiidii-chart-hud__flows">
                    <div className="ns-fiidii-chart-hud__item">
                      <span className="ns-fiidii-chart-hud__label">FII Net:</span>
                      <span className={activeHoverItem.fiiNet >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                        {formatCr(activeHoverItem.fiiNet, true)}
                      </span>
                    </div>
                    <div className="ns-fiidii-chart-hud__item">
                      <span className="ns-fiidii-chart-hud__label">DII Net:</span>
                      <span className={activeHoverItem.diiNet >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                        {formatCr(activeHoverItem.diiNet, true)}
                      </span>
                    </div>
                    <div className="ns-fiidii-chart-hud__item">
                      <span className="ns-fiidii-chart-hud__label">Combined:</span>
                      <span
                        style={{ fontWeight: 700 }}
                        className={activeHoverItem.combinedNet >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}
                      >
                        {formatCr(activeHoverItem.combinedNet, true)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Chart Canvas */}
              <div className="ns-fiidii-chart-canvas">
                {/* Y-Axis scale benchmarks */}
                <div className="ns-fiidii-chart-yaxis">
                  <span className="ns-fiidii-chart-yaxis-tick">+{Math.round(maxChartVal / 1000)}k</span>
                  <span className="ns-fiidii-chart-yaxis-tick ns-fiidii-chart-yaxis-tick--zero">0</span>
                  <span className="ns-fiidii-chart-yaxis-tick">-{Math.round(maxChartVal / 1000)}k</span>
                </div>

                {/* Horizontal Zero Axis Line */}
                <div className="ns-fiidii-chart-axis-line" />

                {/* Columns */}
                {chartItems.map((item, idx) => {
                  const isLatest = idx === chartItems.length - 1;
                  const isHovered = hoveredChartDate === item.date;

                  if (chartMode === 'both') {
                    // Dual mode: FII and DII side-by-side sub-columns
                    const fiiVal = item.fiiNet;
                    const diiVal = item.diiNet;
                    const isFiiPos = fiiVal >= 0;
                    const isDiiPos = diiVal >= 0;
                    const fiiPct = Math.min(100, (Math.abs(fiiVal) / maxChartVal) * 100);
                    const diiPct = Math.min(100, (Math.abs(diiVal) / maxChartVal) * 100);

                    return (
                      <div
                        key={item.date}
                        className={`ns-fiidii-chart-col ${isHovered ? 'is-hovered' : ''}`}
                        onMouseEnter={() => setHoveredChartDate(item.date)}
                        onMouseLeave={() => setHoveredChartDate(null)}
                      >
                        {/* Positive Zone */}
                        <div className="ns-fiidii-chart-zone ns-fiidii-chart-zone--pos">
                          {/* FII Sub-bar */}
                          <div className="ns-fiidii-chart-subcol">
                            {isFiiPos && (
                              <div
                                className="ns-fiidii-bar ns-fiidii-bar--pos"
                                style={{ height: `${Math.max(4, fiiPct)}%` }}
                              >
                                {chartDays <= 20 && (
                                  <span className="ns-fiidii-bar-val ns-fiidii-bar-val--pos">
                                    {formatBarValue(fiiVal)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          {/* DII Sub-bar */}
                          <div className="ns-fiidii-chart-subcol">
                            {isDiiPos && (
                              <div
                                className="ns-fiidii-bar ns-fiidii-bar--pos"
                                style={{ height: `${Math.max(4, diiPct)}%` }}
                              >
                                {chartDays <= 20 && (
                                  <span className="ns-fiidii-bar-val ns-fiidii-bar-val--pos">
                                    {formatBarValue(diiVal)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Negative Zone */}
                        <div className="ns-fiidii-chart-zone ns-fiidii-chart-zone--neg">
                          {/* FII Sub-bar */}
                          <div className="ns-fiidii-chart-subcol">
                            {!isFiiPos && (
                              <div
                                className="ns-fiidii-bar ns-fiidii-bar--neg"
                                style={{ height: `${Math.max(4, fiiPct)}%` }}
                              >
                                {chartDays <= 20 && (
                                  <span className="ns-fiidii-bar-val ns-fiidii-bar-val--neg">
                                    {formatBarValue(fiiVal)}
                                  </span>
                                )}
                              </div>
                            )}
                            <span className="ns-fiidii-chart-subtag">F</span>
                          </div>
                          {/* DII Sub-bar */}
                          <div className="ns-fiidii-chart-subcol">
                            {!isDiiPos && (
                              <div
                                className="ns-fiidii-bar ns-fiidii-bar--neg"
                                style={{ height: `${Math.max(4, diiPct)}%` }}
                              >
                                {chartDays <= 20 && (
                                  <span className="ns-fiidii-bar-val ns-fiidii-bar-val--neg">
                                    {formatBarValue(diiVal)}
                                  </span>
                                )}
                              </div>
                            )}
                            <span className="ns-fiidii-chart-subtag">D</span>
                          </div>
                        </div>

                        {/* Date label */}
                        <span className={`ns-fiidii-chart-label ${isLatest ? 'ns-fiidii-chart-label--latest' : ''}`}>
                          {item.date.slice(8, 10)}/{item.date.slice(5, 7)}
                        </span>
                      </div>
                    );
                  }

                  // Single stream mode: 'combined' | 'fii' | 'dii'
                  const targetVal =
                    chartMode === 'combined'
                      ? item.combinedNet
                      : chartMode === 'fii'
                      ? item.fiiNet
                      : item.diiNet;
                  const isPos = targetVal >= 0;
                  const pct = Math.min(100, (Math.abs(targetVal) / maxChartVal) * 100);

                  return (
                    <div
                      key={item.date}
                      className={`ns-fiidii-chart-col ${isHovered ? 'is-hovered' : ''}`}
                      onMouseEnter={() => setHoveredChartDate(item.date)}
                      onMouseLeave={() => setHoveredChartDate(null)}
                    >
                      {/* Positive Zone */}
                      <div className="ns-fiidii-chart-zone ns-fiidii-chart-zone--pos">
                        {isPos && (
                          <div
                            className="ns-fiidii-bar ns-fiidii-bar--pos"
                            style={{ height: `${Math.max(4, pct)}%` }}
                          >
                            {chartDays <= 20 && (
                              <span className="ns-fiidii-bar-val ns-fiidii-bar-val--pos">
                                {formatBarValue(targetVal)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Negative Zone */}
                      <div className="ns-fiidii-chart-zone ns-fiidii-chart-zone--neg">
                        {!isPos && (
                          <div
                            className="ns-fiidii-bar ns-fiidii-bar--neg"
                            style={{ height: `${Math.max(4, pct)}%` }}
                          >
                            {chartDays <= 20 && (
                              <span className="ns-fiidii-bar-val ns-fiidii-bar-val--neg">
                                {formatBarValue(targetVal)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Date label */}
                      <span className={`ns-fiidii-chart-label ${isLatest ? 'ns-fiidii-chart-label--latest' : ''}`}>
                        {item.date.slice(8, 10)}/{item.date.slice(5, 7)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Chart Footer with Color Legend and Period Stats */}
              <div className="ns-fiidii-chart-footer">
                <div className="ns-fiidii-chart-legend">
                  <div className="ns-fiidii-chart-legend__item">
                    <div className="ns-fiidii-chart-legend__color ns-fiidii-chart-legend__color--pos" />
                    <span>Net Inflow (Positive)</span>
                  </div>
                  <div className="ns-fiidii-chart-legend__item">
                    <div className="ns-fiidii-chart-legend__color ns-fiidii-chart-legend__color--neg" />
                    <span>Net Outflow (Negative)</span>
                  </div>
                </div>

                {chartPeriodStats && (
                  <div className="ns-fiidii-chart-period-stats">
                    <span>
                      {chartDays}D {chartMode === 'fii' ? 'FII' : chartMode === 'dii' ? 'DII' : 'Net'} Total:{' '}
                      <strong className={chartPeriodStats.activeTotal >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}>
                        {formatCr(chartPeriodStats.activeTotal, true)}
                      </strong>
                    </span>
                    <span>
                      Inflow Days:{' '}
                      <strong className="ns-fiidii-val--pos">{chartPeriodStats.activeBullDays}</strong>
                    </span>
                    <span>
                      Outflow Days:{' '}
                      <strong className="ns-fiidii-val--neg">{chartPeriodStats.activeBearDays}</strong>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab 3: 14-Year Monthly Matrix */}
          {activeTab === 'matrix' && (
            <div className="ns-fiidii-matrix-container">
              <div className="ns-fiidii-matrix-tip">
                <span>💡 Click any month to inspect daily datewise institutional breakdown.</span>
              </div>
              <table className="ns-fiidii-matrix-table">
                <thead>
                  <tr>
                    <th className="col-year">Year</th>
                    {MONTH_NAMES.map((m) => (
                      <th key={m}>{m}</th>
                    ))}
                    <th className="col-total">Full Year</th>
                  </tr>
                </thead>
                <tbody>
                  {matrixData.map(({ year, months, total }) => (
                    <tr key={year}>
                      <td className="col-year">{year}</td>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((mNum) => {
                        const val = months[mNum];
                        const formatted =
                          val !== undefined
                            ? `${val > 0 ? '+' : ''}${Math.round(val).toLocaleString('en-IN')}`
                            : '—';
                        const monthKey = `${year}-${String(mNum).padStart(2, '0')}`;
                        const valClass =
                          val !== undefined
                            ? val > 0
                              ? 'ns-fiidii-val--pos'
                              : val < 0
                              ? 'ns-fiidii-val--neg'
                              : ''
                            : '';
                        return (
                          <td
                            key={mNum}
                            className={`${getHeatClass(val)} ${valClass} ${val !== undefined ? 'ns-matrix-cell--clickable' : ''}`}
                            title={`${MONTH_NAMES[mNum - 1]} ${year}: ${val !== undefined ? formatCr(val, true) : 'No data'}${val !== undefined ? ' — Click to view daily datewise records' : ''}`}
                            onClick={() => {
                              if (val !== undefined) {
                                setSelectedMonthFilter(monthKey);
                                setActiveTab('table');
                              }
                            }}
                          >
                            {formatted}
                          </td>
                        );
                      })}
                      <td
                        className={`col-total ${total >= 0 ? 'ns-fiidii-val--pos' : 'ns-fiidii-val--neg'}`}
                        title={`Full Year ${year}: ${formatCr(total, true)}`}
                      >
                        {total > 0 ? '+' : ''}
                        {Math.round(total).toLocaleString('en-IN')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
    </ModalShell>
  );
};

export default FiiDiiExplorerModal;
